// Backend-backed auth + plan persistence. Keeps the same hook surface:
//   { user, hydrated, login, signup, logout, updateUser, setPlan, googleSession }
// All requests use httpOnly cookies (credentials: 'include').
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single configured axios instance — cookies sent on every request.
const http = axios.create({ baseURL: API, withCredentials: true });

// Map backend's snake_case `created_at` into the legacy camelCase `createdAt`
// the existing dashboard pages already read, without changing every page.
const decorate = (u) => (u ? { ...u, createdAt: u.created_at } : u);

const AuthContext = createContext({
  user: null,
  hydrated: false,
  login: async () => {},
  signup: async () => {},
  googleSession: async () => {},
  logout: async () => {},
  updateUser: async () => {},
  setPlan: async () => {},
  refresh: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  // CRITICAL (Emergent OAuth): if returning with session_id in URL hash,
  // skip /auth/me — AuthCallback will exchange it first.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setHydrated(true);
      return;
    }
    (async () => {
      try {
        const { data } = await http.get("/auth/me");
        setUser(decorate(data));
      } catch (_e) {
        setUser(null);
      } finally {
        setHydrated(true);
      }
    })();
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(decorate(data));
      return decorate(data);
    } catch (_e) {
      setUser(null);
      return null;
    }
  }, []);

  const signup = useCallback(async ({ name, email, password }) => {
    const { data } = await http.post("/auth/register", { name, email, password });
    setUser(decorate(data));
    return decorate(data);
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(decorate(data));
    return decorate(data);
  }, []);

  const googleSession = useCallback(async (session_id) => {
    const { data } = await http.post("/auth/google/session", { session_id });
    setUser(decorate(data));
    return decorate(data);
  }, []);

  const logout = useCallback(async () => {
    try { await http.post("/auth/logout"); } catch (_e) { /* ignore */ }
    setUser(null);
  }, []);

  const updateUser = useCallback(async (patch) => {
    const { data } = await http.patch("/me/state", patch);
    setUser(decorate(data));
    return decorate(data);
  }, []);

  // setPlan kept for backwards-compat with dashboard pages, but plan is now
  // only flipped by Stripe webhooks. We just refetch from server.
  const setPlan = useCallback(async () => refresh(), [refresh]);

  return (
    <AuthContext.Provider value={{ user, hydrated, login, signup, googleSession, logout, updateUser, setPlan, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Convenience helper for any direct fetch elsewhere.
export const apiClient = http;

// Format FastAPI error responses (sometimes arrays of {msg}) into a string.
export function formatApiError(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" ");
  if (d?.msg) return d.msg;
  // Network errors don't have a response — give a friendlier hint than "Network Error"
  if (e?.message === "Network Error") {
    return "We couldn't reach the server. Check your connection and try again. If you came from Google sign-in, retry from the login page.";
  }
  return e?.message || fallback;
}
